import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

export default ({ searchInputRef, visibleGroups, onClose }) => {
  const rowElementByIndexRef = useRef(new Map());
  const pendingFocusIndexRef = useRef(null);
  const pendingFocusInputRef = useRef(false);

  const navigate = useNavigate();
  const [activeResultIndex, setActiveResultIndex] = useState(-1);

  const flattenedVisibleItems = useMemo(
    () =>
      visibleGroups
        .filter((group) => group.items.length > 0)
        .flatMap((group) =>
          group.items.map((item) => ({
            groupKey: group.groupKey,
            icon: group.icon,
            item,
          })),
        ),
    [visibleGroups],
  );

  const scrollRowIntoViewByIndex = useCallback((index) => {
    const rowElement = rowElementByIndexRef.current.get(index);
    if (!rowElement) return;

    rowElement.scrollIntoView({ block: 'nearest' });
  }, []);

  const resetActiveResult = useCallback(() => {
    setActiveResultIndex(-1);
    rowElementByIndexRef.current.clear();
    pendingFocusIndexRef.current = null;
    pendingFocusInputRef.current = false;
  }, []);

  const setRowElementByIndex = useCallback((index, element) => {
    if (index < 0) return;

    if (element) {
      rowElementByIndexRef.current.set(index, element);
      return;
    }

    rowElementByIndexRef.current.delete(index);
  }, []);

  useEffect(() => {
    const index = pendingFocusIndexRef.current;

    if (index === null) return;
    if (index !== activeResultIndex) return;

    pendingFocusIndexRef.current = null;
    scrollRowIntoViewByIndex(index);
  }, [activeResultIndex, scrollRowIntoViewByIndex]);

  useEffect(() => {
    if (!pendingFocusInputRef.current) return;
    if (activeResultIndex !== -1) return;

    pendingFocusInputRef.current = false;

    const input = searchInputRef.current;
    if (!input) return;

    input.focus();
    input.select();
  }, [activeResultIndex, searchInputRef]);

  const moveDown = useCallback(() => {
    if (flattenedVisibleItems.length === 0) return;

    setActiveResultIndex((previousIndex) => {
      const nextIndex =
        previousIndex < 0 ? 0 : Math.min(previousIndex + 1, flattenedVisibleItems.length - 1);

      pendingFocusIndexRef.current = nextIndex;
      return nextIndex;
    });
  }, [flattenedVisibleItems.length]);

  const moveUp = useCallback(() => {
    if (flattenedVisibleItems.length === 0) return;

    setActiveResultIndex((previousIndex) => {
      if (previousIndex <= 0) {
        pendingFocusInputRef.current = true;
        return -1;
      }

      const nextIndex = previousIndex - 1;
      pendingFocusIndexRef.current = nextIndex;
      return nextIndex;
    });
  }, [flattenedVisibleItems.length]);

  const openActive = useCallback(
    (event) => {
      if (activeResultIndex < 0) return;

      const active = flattenedVisibleItems[activeResultIndex];
      if (!active?.item?.link) return;

      event.preventDefault();
      navigate(active.item.link);
      onClose?.();
    },
    [activeResultIndex, flattenedVisibleItems, navigate, onClose],
  );

  return {
    activeResultIndex,
    flattenedVisibleItems,
    resetActiveResult,
    setRowElementByIndex,
    moveDown,
    moveUp,
    openActive,
  };
};
